import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { Button } from './Button';

/**
 * Que versão está no ar.
 *
 * Existe por um dia concreto: um `git pull` abortou em silêncio na VPS, o
 * deploy reconstruiu o código VELHO, e o Diretor passou meia hora tentando
 * usar uma tela que ainda não existia lá. Sem isto, a única forma de saber o
 * que subiu é entrar por SSH.
 *
 * Painel e API sobem separados e PODEM divergir — é justamente o que acontece
 * num deploy pela metade. Por isso os dois aparecem, e a diferença é gritada.
 */
interface Health {
  status: string;
  version: string;
  gitSha: string;
  startedAt: string;
  database: string;
}

/** Gravadas no bundle em tempo de build (ver docker/admin.Dockerfile). */
const PAINEL_VERSAO = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'desenvolvimento';
const PAINEL_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? 'local';

/**
 * Quando ESTE código chegou ao navegador.
 *
 * É o que separa dois casos que pareciam o mesmo. O commit do painel está
 * congelado dentro do bundle; o da API é buscado ao vivo, e a cada foco da
 * janela. Então, com a aba aberta na hora de um deploy, os dois SEMPRE
 * divergem — e a primeira versão deste cartão acusava "deploy pela metade" e
 * mandava o Diretor rodar o deploy de novo na VPS. O deploy estava perfeito;
 * velha era a aba dele.
 *
 * Com a hora em que o bundle carregou dá para decidir: se a API subiu DEPOIS
 * disto, o painel é velho por definição e a saída é recarregar. Só quando a
 * API é mais antiga que esta aba é que a divergência é de verdade — aí o
 * deploy subiu a API e deixou o painel para trás.
 */
const CARREGADO_EM = Date.now();

const curto = (sha: string) => (sha.length > 7 ? sha.slice(0, 7) : sha);

/** Linha rótulo → valor. A `.stat-row` do sistema é para blocos lado a lado. */
function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '7px 0',
        borderBottom: `1px solid ${T.line}`,
        fontSize: 13,
      }}
    >
      <span style={{ color: T.inkFaint }}>{rotulo}</span>
      <span className="mono" style={{ fontSize: 12.5, textAlign: 'right', wordBreak: 'break-all' }}>
        {children}
      </span>
    </div>
  );
}

export function VersionCard() {
  const query = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Health>('/health'),
    // O operador abre esta tela para conferir um deploy que ACABOU de rodar.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const api_ = query.data;
  const shaApi = api_ ? curto(api_.gitSha) : null;
  const shaPainel = curto(PAINEL_SHA);

  // Só dá para comparar quando os dois têm commit de verdade. Em
  // desenvolvimento cada um roda de um jeito e divergir é o normal.
  const comparavel = Boolean(shaApi) && shaApi !== 'local' && shaPainel !== 'local';
  const divergente = comparavel && shaApi !== shaPainel;

  // A API reiniciou depois que esta aba carregou: o deploy passou por baixo de
  // quem já estava com a tela aberta. O painel não está errado — está velho.
  const apiSubiuDepois = Boolean(
    api_?.startedAt && new Date(api_.startedAt).getTime() > CARREGADO_EM,
  );
  const sóRecarregar = divergente && apiSubiuDepois;

  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div className="panel__title" style={{ margin: 0 }}>
          Versão no ar
        </div>
        <Button
          small
          variant="ghost"
          icon={RefreshCw}
          aria-label="Conferir de novo"
          title="Conferir de novo"
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        />
      </div>

      {sóRecarregar && (
        <div className="alert alert--warn" role="status" style={{ marginBottom: 14 }}>
          <strong>Subiu uma versão nova enquanto esta aba estava aberta.</strong> O painel que você
          está usando é o de antes. Recarregue com Ctrl+F5 — o deploy está inteiro.
        </div>
      )}

      {divergente && !sóRecarregar && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          <strong>Deploy pela metade.</strong> A API subiu num commit e o painel ficou noutro, e
          isto não é aba velha: a API está no ar desde antes desta página carregar. Recarregue com
          Ctrl+F5 para confirmar; se continuar, rode o deploy de novo na VPS.
        </div>
      )}

      <Linha rotulo="Painel">
          {PAINEL_VERSAO}
          {shaPainel !== 'local' && (
            <span style={{ color: T.inkFaint }}> · {shaPainel}</span>
          )}
      </Linha>

      <Linha rotulo="API">
          {query.isPending && 'consultando…'}
          {query.isError && <span style={{ color: T.red }}>não respondeu</span>}
          {api_ && (
            <>
              {api_.version}
              {shaApi !== 'local' && <span style={{ color: T.inkFaint }}> · {shaApi}</span>}
            </>
          )}
      </Linha>

      {api_ && (
        <>
          <Linha rotulo="No ar desde">{formatDateTime(api_.startedAt)}</Linha>
          <Linha rotulo="Banco">
              {api_.database === 'up' ? (
                <span style={{ color: T.green, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <CheckCircle2 size={14} aria-hidden /> respondendo
                </span>
              ) : (
                <span style={{ color: T.red, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <AlertTriangle size={14} aria-hidden /> fora
                </span>
              )}
          </Linha>
        </>
      )}

      {PAINEL_VERSAO.endsWith('-modificado') && (
        <p className="muted" style={{ fontSize: 12, marginTop: 12, color: T.amber }}>
          Esta imagem foi construída com arquivos alterados à mão na VPS, fora do
          que está commitado. O próximo deploy limpo desfaz isso.
        </p>
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 12, color: T.inkFaint }}>
        Depois de um deploy, recarregue a página (Ctrl+F5) e confira se o commit
        mudou. Se não mudou, o que está no ar continua sendo o de antes.
      </p>
    </div>
  );
}
